import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { selectVerificationMethod } from '@/store/slices/authSlice';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Phone } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@radix-ui/react-radio-group';

const VerificationMethod = ({ user }) => {
  const [selectedMethod, setSelectedMethod] = useState('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await dispatch(selectVerificationMethod(selectedMethod)).unwrap();
    } catch (err) {
      setError(err.message || 'Failed to select verification method');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-2xl">Verify Your Account</CardTitle>
        <CardDescription>
          Choose how you want to receive your verification code
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <RadioGroup
            value={selectedMethod}
            onValueChange={setSelectedMethod}
            className="space-y-3"
          >
            <div className="flex items-center space-x-3 p-4 border rounded-lg">
              <RadioGroupItem value="email" id="email" />
              <Label htmlFor="email" className="flex items-center space-x-2 flex-1 cursor-pointer">
                <Mail className="h-5 w-5" />
                <div>
                  <div className="font-medium">Email Verification</div>
                  <div className="text-sm text-muted-foreground">Send code to {user.email}</div>
                </div>
              </Label>
            </div>

            <div className="flex items-center space-x-3 p-4 border rounded-lg">
              <RadioGroupItem value="phone" id="phone" />
              <Label htmlFor="phone" className="flex items-center space-x-2 flex-1 cursor-pointer">
                <Phone className="h-5 w-5" />
                <div>
                  <div className="font-medium">SMS Verification</div>
                  <div className="text-sm text-muted-foreground">Send code to {user.phone}</div>
                </div>
              </Label>
            </div>
          </RadioGroup>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Sending...' : 'Send Verification Code'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default VerificationMethod;